import React, { useCallback } from 'react'

import Flex from '@react-css/flex'
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'

import progressCompleteIcon from '../../../../../assets/images/progress_complete_check.svg'
import { Icon } from '../../../../common/Icon'
import { ESTIMATED_PROGRESS_TOOLTIP } from '../../../../common/Table/buildProgressTableData'

import type { ProgressCellProps } from '../../../../common/Table/progressTableTypes'

export const ProgressCell = ({
  progress,
  total,
  rate,
  units,
  showRate = true,
  displayUnits = false,
  onClick,
  rateLabel,
  totalCompletedLabel,
  isEstimatedOverview,
  estimatedTooltipText = ESTIMATED_PROGRESS_TOOLTIP,
  showGreenCompleteUI = true,
}: ProgressCellProps) => {
  const handleClick = useCallback(() => {
    if (onClick) onClick()
  }, [onClick])

  if (!total) {
    return <ProgressPrintout>N/A</ProgressPrintout>
  }

  const isNumericallyFull =
    total > 0 && Math.floor((progress / total) * 100) >= 100

  const useGreenCompleteStyle =
    isNumericallyFull && showGreenCompleteUI !== false

  const rateSublineContent =
    totalCompletedLabel != null ? (
      <SublineGroup>
        <Subline>{rateLabel ?? ''}</Subline>
        <Subline>{totalCompletedLabel}</Subline>
      </SublineGroup>
    ) : (
      <>
        {rateLabel != null ? (
          <ProgressPrintout>{rateLabel}</ProgressPrintout>
        ) : (
          !!units() && (
            <ProgressPrintout>
              {rate}
              {units()}
            </ProgressPrintout>
          )
        )}
      </>
    )

  return (
    <div
      style={{ minWidth: '150px', cursor: onClick ? 'pointer' : 'default' }}
      onClick={handleClick}
    >
      <BarRow>
        <Flex>
          {isEstimatedOverview && (
            <EstimatedIconWrap
              data-tip={estimatedTooltipText}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <EstimatedInfoChar aria-hidden>i</EstimatedInfoChar>
            </EstimatedIconWrap>
          )}
          <ProgressBarAndStat
            progress={progress}
            total={total}
            onClick={onClick}
            useGreenCompleteStyle={useGreenCompleteStyle}
          />
        </Flex>
      </BarRow>
      {showRate && (
        <Sublines>
          {!isNumericallyFull && progress !== 0 && rateSublineContent}
          {progress === 0 && (
            <SublineGroup>
              <ProgressPrintout>N/A</ProgressPrintout>
              {displayUnits && (
                <ProgressPrintout>
                  {totalCompletedLabel ?? `0 of ${total.toLocaleString()}`}
                </ProgressPrintout>
              )}
            </SublineGroup>
          )}
          {isNumericallyFull && useGreenCompleteStyle && (
            <>
              {totalCompletedLabel != null ? (
                <SublineGroup>
                  <Subline>Complete</Subline>
                  <Subline>{totalCompletedLabel}</Subline>
                </SublineGroup>
              ) : (
                <ProgressPrintout>Complete</ProgressPrintout>
              )}
            </>
          )}
          {isNumericallyFull && !useGreenCompleteStyle && progress !== 0 && (
            <>{rateSublineContent}</>
          )}
        </Sublines>
      )}
    </div>
  )
}

const BarRow = styled.div`
  height: 16px;
  display: flex;
  align-items: center;

  & > div {
    flex: 1;
    min-width: 0;
  }
`

const EstimatedIconWrap = styled.span`
  flex-shrink: 0;
  margin-right: 4px;
  cursor: help;
  display: inline-flex;
  align-items: center;
  line-height: 0;
`

const EstimatedInfoChar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  font-size: 11px;
  font-weight: bold;
  color: #1e40af;
  background: #e0e7ff;
  border-radius: 50%;
`

const StatWrap = styled.div`
  display: flex;
  align-items: center;
  line-height: 1;
  font-weight: bold;
`

const Sublines = styled.div`
  padding-top: 5px;
`

const SublineGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const Subline = styled.div`
  color: #94a3b8;
  font-size: 0.8em;
  padding: 0 5px;
`

const ProgressPrintout = styled.div`
  color: #94a3b8;
  font-size: 0.8em;
  padding: 0 5px 5px 5px;
`

interface ProgressBarAndStatProps {
  progress: number
  total: number
  onClick?: () => void
  useGreenCompleteStyle: boolean
}

function ProgressBarAndStat({
  progress,
  total,
  onClick,
  useGreenCompleteStyle,
}: ProgressBarAndStatProps) {
  const pct = Math.floor((progress / total) * 100)
  return (
    <>
      <ResponsiveContainer width="90%" height={10}>
        <BarChart
          barSize={4}
          style={{ cursor: onClick ? 'pointer' : 'default' }}
          data={[
            {
              value: (progress / total) * 100,
              incomplete: 100 - (progress / total) * 100,
            },
          ]}
          layout="vertical"
        >
          <XAxis type="number" hide xAxisId={0} domain={[0, 100]} />
          <YAxis
            yAxisId={0}
            width={0}
            dataKey="name"
            type="category"
            axisLine={false}
            tickLine={false}
          />
          <Bar
            dataKey="value"
            fill={useGreenCompleteStyle ? '#10B981' : '#1E40AF'}
            radius={8}
            background={{ radius: 8 }}
          />
        </BarChart>
      </ResponsiveContainer>
      <StatWrap
        style={{
          color: progress !== 0 ? '#1E40AF' : '#94A3B8',
        }}
      >
        {useGreenCompleteStyle ? (
          <Icon icon={progressCompleteIcon} size={16} />
        ) : (
          `${pct}%`
        )}
      </StatWrap>
    </>
  )
}
